<script>
  import { onMount, tick } from "svelte";
  import * as d3 from "d3";
  import rewind from "@turf/rewind";

  //export let data;        // artist100
  let width = 1280;
  let height = 720;

  let container;
  let svg, g, projection, path, zoom, nodes;
  let centroids = {};
  let world_geojson;
  let label1, label2, label3;
  let labels;
  let labelsEnabled = false;
  let selectedCountry = null;
  let simulation;

  import artist100 from "../data/spotify_data_visualization_artist_test.json";

// Color scheme
  function spotifyGreenShades(n) {
    const hue = 141;      
    const saturation = 65;
    const minLight = 30;   // darker
    const maxLight = 55;   // lighter

    return Array.from({ length: n }, (_, i) => {
      const lightness =
        minLight + (i / (n - 1)) * (maxLight - minLight);

      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    });
  }

  const countries = [...new Set(artist100.map(d => d.Country))]
    .sort(d3.ascending);

  const colors = spotifyGreenShades(countries.length);

  const countryColors = d3.scaleOrdinal()
  .domain(countries)
  .range(colors);
  //const colors = spotifyGreenRange(countries.length);


  function zoomToCountry(countryName) {
    svg.interrupt();
    g.selectAll("*").interrupt();
    nodes.interrupt();

    const containerWidth = container.clientWidth;
    const isMobile = containerWidth <= 480;
    const scaleK = isMobile ? 10 : 5;
    const viewHeight = isMobile ? height * 2.5 : height;

    selectedCountry = countryName;

    g.selectAll(".country")
      .transition()
      .duration(2000)
      .attr("fill", d =>
      !selectedCountry
        ? "#808080"
        : d.properties.NAME === selectedCountry
          ? "#bbbbbb"
          : "#505050"
    );

    if (!countryName) {
      if (simulation) simulation.stop();

      // Reset view
      svg.transition()
        .duration(2000)
        .call(
          zoom.transform, 
          d3.zoomIdentity
            .translate(width / 2, viewHeight / 2)
            .scale(1.05) 
            .translate((-width / 2) - 30, -height / 2));

      // Show all bubbles again
      nodes
        .transition()
        .duration(2000)
        .style("opacity", 1)
        .style("pointer-events", "all");

      label1.style("opacity", labelsEnabled ? 1 : 0);
      label2.style("opacity", labelsEnabled ? 0.8 : 0);
      label3.style("opacity", 0);
      return;
  }

  const [cx, cy] = centroids[countryName];
  if (!cx || !cy) return;

  svg.transition()
    .duration(2000)
    .call(
      zoom.transform,
      d3.zoomIdentity
        .translate(width / 2, viewHeight / 2)
        .scale(scaleK)
        .translate(-cx, -cy)
    );

  nodes
    .transition()
    .duration(2000)
    .style("opacity", d => d.Country === countryName ? 1 : 0.05)
    .style("pointer-events", d => d.Country === countryName ? "all" : "none");

  g.selectAll(".label-artist, .label-streams")
    .style("opacity", d =>
      labelsEnabled &&
      (!selectedCountry || d.Country === selectedCountry)
        ? 1
        : 0
    );

  g.selectAll(".label-country")
    .style("opacity", 0);
}
  
onMount(async () => {
  const res = await fetch("./world.geojson");
  const raw = await res.json();

  world_geojson = rewind(raw, { reverse: true });

  await tick();


  initSVG();
  drawMap();
  drawBubbles();

  d3.select("#countrySelect").on("change", null); // clear old
  d3.select("#countrySelect").on("change", function () {
    zoomToCountry(this.value);
  });

});

  // STEP 1 — INITIALIZE SVG
  function initSVG() {

    const containerWidth = container.clientWidth;
    const isMobile = containerWidth <= 480;
    const viewHeight = isMobile ? height * 2.5 : height;

    const minZoom = isMobile ? 1.0 : 0.9;
    const maxZoom = isMobile ? 18 : 10;
    const initialScale = isMobile ? 1.05 : 0.9;

    svg = d3
      .select(container)
      .append("svg")
      .attr("viewBox", `0 0 ${width} ${viewHeight}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // EVERYTHING goes inside this group
    g = svg.append("g")
      .attr("class", "map-layer");

    let fontSize = d3.scaleLinear().domain([2, 24]).range([0.5, 5]).clamp(true);
    let streamFontSize = d3.scaleLinear().domain([2, 24]).range([0.4, 4]).clamp(true);
    let labelsVisible = false;

    zoom = d3.zoom()
             .scaleExtent([minZoom, maxZoom])
             .on("zoom", (event) => {
              const k = event.transform.k;

            g.attr("transform", event.transform);

            labelsEnabled = k > 4;

            g.selectAll(".label-artist")
             .style("font-size", d => `${fontSize(d.r)}px`)
             .style("opacity", d =>
              labelsEnabled &&
              (!selectedCountry || d.Country === selectedCountry)
              ? 1
              : 0
              );
              
            g.selectAll(".label-streams")
            .style("font-size", d => `${streamFontSize(d.r)}px`)
             .style("opacity", d =>
              labelsEnabled &&
              (!selectedCountry || d.Country === selectedCountry)
              ? 1
              : 0
              );

            g.selectAll(".label-country")
              .style("font-size", d => `${fontSize(d.r)}px`);
          });
        svg.call(zoom)

        svg.call(
          zoom.transform,
          d3.zoomIdentity
            .translate(width / 2, viewHeight / 2)
            .scale(initialScale) 
            .translate((-width / 2) - 30, -height / 2)
        );
  }


  // STEP 2 — DRAW WORLD MAP
  function drawMap() {
    
    const isMobile = window.innerWidth <= 480;
    const viewHeight = isMobile ? height * 2.5 : height;

    projection = d3.geoNaturalEarth1()
      .fitSize([width, viewHeight], world_geojson)
      .clipExtent([[0, 0], [width, height]]);

    projection.translate([width / 2, height / 2]);

    path = d3.geoPath(projection);


    // Draw each country
    g.append("g")
      .attr("class", "countries")
      .selectAll("path")
      .data(world_geojson.features)
      .join("path")
      .attr("class", "country")
      .attr("data-name", d => d.properties.NAME)
      .attr("d", path)
      .attr("stroke", "#0F0F0F")
      .attr("stroke-width", 0.5)
      .attr("fill", "#808080");

    // Precompute centroids
    world_geojson.features.forEach(f => {
      const cname = f.properties.NAME;
      let mainPolygon;

      if (f.geometry.type === "Polygon") {
        mainPolygon = f;
      } else if (f.geometry.type === "MultiPolygon") {
        // pick the polygon with largest area
      const areas = f.geometry.coordinates.map(coords => {
        return d3.polygonArea(coords[0]); // coords[0] = outer ring
      });
    
      const maxIndex = areas.indexOf(Math.max(...areas));
      mainPolygon = {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: f.geometry.coordinates[maxIndex] }
      };
    }

      centroids[cname] = path.centroid(mainPolygon);
    });
  }

  // STEP 3 — DRAW BUBBLES
  function drawBubbles() {
    const bubbles = artist100
      .filter(d => d.Streams >= 100)
      .map((d, i) => ({
      ...d,
      id: i,
      x: centroids[d.Country][0] + (Math.random() - 0.5) * 10,
      y: centroids[d.Country][1] + (Math.random() - 0.5) * 10,
      visible: true
  }))
  .filter(d => centroids[d.Country]);

    const maxStreams = d3.max(bubbles, d => d.Streams);
    const radius = d3.scaleSqrt().domain([100, maxStreams]).range([3, 25]);
    let fontSize = d3.scaleLinear().domain([2, 24]).range([0.5, 5]).clamp(true);
    let streamFontSize = d3.scaleLinear().domain([2, 24]).range([0.4, 4]).clamp(true);

    bubbles.forEach(d => {
      const nameLengthFactor = Math.max(0.6, 1 - d.Artist.length * 0.02);

      d.artistFont = fontSize(d.r) * nameLengthFactor;
      d.streamFont = streamFontSize(d.r);
    });


    nodes = g.append("g")
      .attr("class", "bubbles")
      .selectAll("circle")
      .data(bubbles)
      .join("circle")
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", d => d.r = radius(d.Streams)) 
      .attr("fill", d => d.baseColor = countryColors(d.Country)) 
      .attr("fill-opacity", 0.8)
      .attr("stroke", "white")
      .attr("stroke-width", 0.5)
      .on("mouseenter", function (event, d) {
        d3.select(this)
          .raise()
          .transition()
          .duration(400)
          .attr("r", d.r + 5)
          .attr("fill", "#FFFFFF")
          .attr("stroke", "#1DB954")
          .style("filter", "drop-shadow(0 0 6px rgba(29,185,84,0.7))");

        if (labelsEnabled) {
          label1
            .filter(l => l.id === d.id)
            .attr("fill", "#000")
            .style("font-size", d => `${fontSize(d.r) + 1}px`);
          label2
            .filter(l => l.id === d.id)
            .attr("fill", "#000")
            .style("font-size", d => `${streamFontSize(d.r) + 1}px`);
          label3
            .filter(l => l.id === d.id)
            .attr("fill", "#000")
            .style("opacity", 1)
            .style("font-size", d => `${fontSize(d.r) + 0.5}px`);
        }
      })
      .on("mouseleave", function (event, d) {
        d3.select(this)
          .transition()
          .duration(400)
          .attr("r", d.r)
          .attr("fill", d.baseColor)
          .attr("stroke", "#FFFFFF")
          .style("filter", "none");

        label1
          .filter(l => l.id === d.id)
          .attr("fill", "#fff")
          .style("font-size", d => `${fontSize(d.r)}px`);
        label2
          .filter(l => l.id === d.id)
          .attr("fill", "#fff")
          .style("font-size", d => `${streamFontSize(d.r)}px`);
        label3
          .filter(l => l.id === d.id)
          .attr("fill", "#fff")
          .style("opacity", 0)
          .style("font-size", d => `${fontSize(d.r)}px`);
      });

    label1 = g.append("g")
      .selectAll("text")
      .data(bubbles)
      .join("text")
      .attr("class", "bubble-label label-artist")
      .attr("x", d => d.x)
      .attr("y", d => d.y)
      .attr("dy", "0.1em")
      .text(d => d.Artist)
      .attr("text-anchor", "middle")
      .attr("fill", "#ffffff")
      .style("font-family", "Outfit, sans-serif")
      .style("font-size", d => `${d.artistFont}px`)
      .style("font-weight", 700)
      .style("pointer-events", "none")
      .style("opacity", 0)

    label2 = g.append("g")
      .selectAll("text")
      .data(bubbles)
      .join("text")
      .attr("class", "bubble-label label-streams")
      .attr("x", d => d.x)
      .attr("y", d => d.y)
      .attr("dy", "1.5em")
      .text(d => `${d.Streams} streams`)
      .attr("text-anchor", "middle")
      .attr("fill", "#ffffff")
      .style("font-family", "Outfit, sans-serif")
      .style("font-size", d => `${d.streamFont}px`)
      .style("font-weight", 300)
      .style("pointer-events", "none")
      .style("opacity", 0);

    label3 = g.append("g")
      .selectAll("text")
      .data(bubbles)
      .join("text")
      .attr("class", "bubble-label label-country")
      .attr("x", d => d.x)
      .attr("y", d => d.y)
      .attr("dy", "-1.8em")
      .text(d => d.Country)
      .attr("text-anchor", "middle")
      .attr("fill", "#ffffff")
      .style("font-family", "Outfit, sans-serif")
      .style("font-size", d => `${d.streamFont}px`)
      .style("font-weight", 300)
      .style("pointer-events", "none")
      .style("opacity", 0);

  let tickCount = 0;

  function ticked() {
    tickCount++;

    nodes
      .attr("cx", d => d.x)
      .attr("cy", d => d.y);

    if (tickCount % 3 === 0) {
      /*
      labels.attr("x", d => d.x).attr("y", d => d.y);
      */
      label1.attr("x", d => d.x).attr("y", d => d.y);
      label2.attr("x", d => d.x).attr("y", d => d.y);
      label3.attr("x", d => d.x).attr("y", d => d.y);
    }
  }

  // Force simulation to separate overlapping bubbles

  let ticks = 0;

  simulation = d3.forceSimulation(bubbles)
    .force("x", d3.forceX(d => centroids[d.Country][0]).strength(1))
    .force("y", d3.forceY(d => centroids[d.Country][1]).strength(1))
    .force("collision", d3.forceCollide().radius(d => d.r + 2))
    .on("tick", ticked);
}

console.log(width, height);
</script>

  <div class="map-wrapper">
    <div class="map-header">
      <h2> Origin of the artists
        <span class = "h2-small">over 100 streams</span>
      </h2>
      
      <div class="map-control">
        <label>See artists from</label>
        <select
          bind:value={selectedCountry}
          on:change={(e) => zoomToCountry(e.target.value)}
        >
          <option value="">All countries</option>
          {#each countries as c}
            <option value={c}>{c}</option>
          {/each}
        </select>
      </div>
    </div>

    <div bind:this={container} class="bubble-wrapper"></div>
  </div>

<style>
  :global(svg) {
    width: 100%;
    height: 100%;
  }

  .map-wrapper {
    background: #202020;
  }


  .map-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  .map-control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .bubble-wrapper {
    background: #0F0F0F;
    border-radius: 20px;
    width: 100%;
    height: 100%;
    /*overflow: hidden;*/
  }

  select {
    border: 1px solid transparent;
    border-radius: 20px;
    padding: 0.2em;
    box-shadow: 0px 0px 2px 2px #1DB954;
    font-family: "Outfit", sans-serif;
    font-weight: 600;
    font-size: 0.9rem;
    color: #FFFFFF;
    background: #202020;
  }

  label {
    margin: 0;
    padding: 2.5px;
    font-family: "Outfit", sans-serif;
    font-weight: 600;
    font-size: 0.9rem;
    color: #FFFFFF;
    background: #202020;
  }

  h2 {
    color: #FFFFFF;
    font-family: "Outfit", sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0;
  }

  .h2-small {
    font-size: 0.8rem;
    font-style: italic;
    font-weight: 100;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background: #3F3F3F;
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    .map-control {
      margin-bottom: 0.5rem;
    }
  }

  @media (max-width: 480px) {
    .map-header {
      flex-direction: column;
      align-items: flex-start; 
      width: 100%;
    }
    
    .map-control {
      margin-bottom: 0.5rem;
      align-self: flex-end;
    }

    h2 {
      font-size: 1.125rem;
      margin-top: 0;
    }

    .h2-small {
      font-size: 0.6rem;
    }

    select {
      border: 1px solid #1DB954;
      padding: 0.2rem;
      padding-left: 0.4rem;
      font-size: 0.75rem;    
      color: #1DB954;
    }

    label {
      font-size: 0.75rem;
    }

  }

</style>